import { arrange, modulePath, type IconName, type ModuleAccent } from '@nexus/core';
import type { SessionInfo } from '../api/types';
import { canIn } from '../session/session';
import type { WebModule } from './define';
import { managesModuleSettings, moduleSettingsPath, settingsOfModule } from './settings';

export interface NavLink {
  /** i18n key. */
  label: string;
  path: string;
  icon: IconName;
}

export interface NavModule extends NavLink {
  id: string;
  accent?: ModuleAccent;
  /** Pages of the module and of its submodules. */
  items: NavLink[];
}

export function accentOf(module: WebModule, all: readonly WebModule[]): ModuleAccent | undefined {
  if (module.manifest.theme) return module.manifest.theme.accent;
  const parent = all.find((m) => m.manifest.id === module.manifest.parent);
  return parent?.manifest.theme?.accent;
}

/**
 * Navigation from the module manifests: only modules active for the company, only entries the
 * person has the permission for, in the order the company set (docs/architecture/modules.md). The
 * kernel adds the settings page of a module as its last entry when it has settings the person
 * sees.
 */
export function buildNavigation(modules: readonly WebModule[], session: SessionInfo): NavModule[] {
  const active = new Set(session.modules);
  const visible = (permission?: string) => !permission || canIn(session, permission);

  const { visible: ordered } = arrange(
    modules
      .filter((m) => !m.manifest.parent && active.has(m.manifest.id))
      .map((module) => ({ key: module.manifest.id, module })),
    { order: session.moduleOrder ?? [], hidden: [] },
  );
  return ordered
    .map(({ module }) => {
      const subs = modules.filter(
        (m) => m.manifest.parent === module.manifest.id && active.has(m.manifest.id),
      );
      const items = [module, ...subs].flatMap((m) =>
        (m.manifest.navigation ?? [])
          .filter((item) => visible(item.permission))
          .map((item) => ({
            label: item.label,
            path: item.path,
            icon: item.icon ?? m.manifest.icon,
          })),
      );
      const manifests = modules.map((m) => m.manifest);
      if (
        items.length > 0 &&
        (settingsOfModule(module.manifest, manifests, session).personal.length > 0 ||
          managesModuleSettings(module.manifest, manifests, session))
      ) {
        items.push({
          label: 'shell.moduleSettings',
          path: moduleSettingsPath(module.manifest.id),
          icon: 'settings',
        });
      }
      return {
        id: module.manifest.id,
        label: module.manifest.name,
        path: items[0]?.path ?? modulePath(module.manifest.id),
        icon: module.manifest.icon,
        accent: accentOf(module, modules),
        items,
      };
    })
    .filter((module) => module.items.length > 0);
}

/** The module a path belongs to, for the accent of the content area. */
export function moduleForPath(
  navigation: readonly NavModule[],
  path: string,
): NavModule | undefined {
  return navigation.find(
    (module) => path === modulePath(module.id) || path.startsWith(`${modulePath(module.id)}/`),
  );
}
